import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Button,
  Message,
  Space,
  Card,
  Upload,
  Tag,
  Progress,
  Checkbox
} from '@arco-design/web-react';
import { IconHome, IconFile, IconUpload, IconCheck, IconClose } from '@arco-design/web-react/icon';
import { useSaveCorporateDetails, useUploadDocument, useDeleteDocument, useGeographyStates } from '../hooks/useOnboardingData';
import { getApiUrl } from '@/config/api';
import {
  COUNTRY_OPTIONS,
  COUNTRY_STATE_OPTIONS,
  buildStateOptions,
  getCityOptions,
  getCountryCode
} from '../data/geography';
import './TeacherDetailsStep.css';
import './DocumentUploadCard.css';
import DocumentPreviewModal from './DocumentPreviewModal';
import ReportProblemButton from '../components/ReportProblemButton';

const FormItem = Form.Item;
const { Option } = Select;
const CUSTOM_OPTION_VALUE = '__CUSTOM__';

const RFC_REGEX = /^[A-Z&Ñ]{3}[0-9]{6}[A-Z0-9]{3}$/;

function CorporateDetailsStep({
  onNext,
  onBack,
  initialData,
  documents: initialDocuments,
  requestedChanges,
  previewMode = false,
  freeMode = false
}) {
  const { t } = useTranslation('onboarding');
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [useSameFiscalAddress, setUseSameFiscalAddress] = useState(false);
  const [commercialCountryCode, setCommercialCountryCode] = useState('MX');
  const [commercialCityOptions, setCommercialCityOptions] = useState([]);
  const [commercialStateValue, setCommercialStateValue] = useState('');
  const [commercialUseCustomState, setCommercialUseCustomState] = useState(false);
  const [commercialUseCustomCountry, setCommercialUseCustomCountry] = useState(false);
  const [commercialUseCustomCity, setCommercialUseCustomCity] = useState(false);
  const [fiscalCountryCode, setFiscalCountryCode] = useState('MX');
  const [fiscalCityOptions, setFiscalCityOptions] = useState([]);
  const [fiscalStateValue, setFiscalStateValue] = useState('');
  const [fiscalUseCustomState, setFiscalUseCustomState] = useState(false);
  const [fiscalUseCustomCountry, setFiscalUseCustomCountry] = useState(false);
  const [fiscalUseCustomCity, setFiscalUseCustomCity] = useState(false);

  const saveMutation = useSaveCorporateDetails();
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();
  const commercialStatesQuery = useGeographyStates(commercialCountryCode);
  const fiscalStatesQuery = useGeographyStates(fiscalCountryCode);

  const commercialStateOptions = useMemo(() => {
    if (commercialCountryCode === 'MX') return commercialStatesQuery.data || [];
    if (COUNTRY_STATE_OPTIONS[commercialCountryCode]) return buildStateOptions(COUNTRY_STATE_OPTIONS[commercialCountryCode]);
    return [];
  }, [commercialCountryCode, commercialStatesQuery.data]);

  const fiscalStateOptions = useMemo(() => {
    if (fiscalCountryCode === 'MX') return fiscalStatesQuery.data || [];
    if (COUNTRY_STATE_OPTIONS[fiscalCountryCode]) return buildStateOptions(COUNTRY_STATE_OPTIONS[fiscalCountryCode]);
    return [];
  }, [fiscalCountryCode, fiscalStatesQuery.data]);

  const changeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'corporate' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);

  const documentChangeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'documents' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);

  const getFieldProps = (field) => {
    const message = changeMap[field];
    if (!message) return {};
    return {
      validateStatus: 'error',
      help: message
    };
  };

  useEffect(() => {
    if (initialData) {
      const commercialCountry = initialData.commercial_country || 'México';
      const fiscalCountry = initialData.fiscal_country || 'México';
      const commercialIsKnown = COUNTRY_OPTIONS.some((option) => option.value === commercialCountry);
      const fiscalIsKnown = COUNTRY_OPTIONS.some((option) => option.value === fiscalCountry);
      const commercialCode = getCountryCode(commercialCountry) || 'MX';
      const fiscalCode = getCountryCode(fiscalCountry) || 'MX';
      form.setFieldsValue({
        company_legal_name: initialData.company_legal_name || '',
        trade_name: initialData.trade_name || '',
        rfc: initialData.rfc || '',
        industry: initialData.industry || '',
        website: initialData.website || '',
        commercial_address: initialData.commercial_address || '',
        commercial_city: initialData.commercial_city || '',
        commercial_city_other: '',
        commercial_state: initialData.commercial_state || '',
        commercial_postal_code: initialData.commercial_postal_code || '',
        commercial_country: commercialIsKnown ? commercialCountry : CUSTOM_OPTION_VALUE,
        commercial_country_other: commercialIsKnown ? '' : commercialCountry,
        fiscal_address: initialData.fiscal_address || '',
        fiscal_city: initialData.fiscal_city || '',
        fiscal_city_other: '',
        fiscal_state: initialData.fiscal_state || '',
        fiscal_postal_code: initialData.fiscal_postal_code || '',
        fiscal_country: fiscalIsKnown ? fiscalCountry : CUSTOM_OPTION_VALUE,
        fiscal_country_other: fiscalIsKnown ? '' : fiscalCountry
      });
      setCommercialCountryCode(commercialCode);
      setFiscalCountryCode(fiscalCode);
      setCommercialStateValue(initialData.commercial_state || '');
      setFiscalStateValue(initialData.fiscal_state || '');
      setCommercialUseCustomCountry(!commercialIsKnown);
      setFiscalUseCustomCountry(!fiscalIsKnown);
    }
  }, [initialData, form]);

  useEffect(() => {
    if (!commercialStateValue) {
      setCommercialCityOptions([]);
      setCommercialUseCustomCity(false);
      return;
    }
    setCommercialCityOptions(getCityOptions(commercialCountryCode, commercialStateValue));
  }, [commercialCountryCode, commercialStateValue]);

  useEffect(() => {
    if (!fiscalStateValue) {
      setFiscalCityOptions([]);
      setFiscalUseCustomCity(false);
      return;
    }
    setFiscalCityOptions(getCityOptions(fiscalCountryCode, fiscalStateValue));
  }, [fiscalCountryCode, fiscalStateValue]);

  useEffect(() => {
    if (!commercialStateValue) return;
    const isKnown = commercialStateOptions.some((option) => option.value === commercialStateValue);
    setCommercialUseCustomState(!isKnown);
  }, [commercialStateOptions, commercialStateValue]);

  useEffect(() => {
    if (!fiscalStateValue) return;
    const isKnown = fiscalStateOptions.some((option) => option.value === fiscalStateValue);
    setFiscalUseCustomState(!isKnown);
  }, [fiscalStateOptions, fiscalStateValue]);

  useEffect(() => {
    setDocuments(initialDocuments || {});
  }, [initialDocuments]);

  const syncFiscalWithCommercial = () => {
    const values = form.getFieldsValue();
    form.setFieldsValue({
      fiscal_address: values.commercial_address || '',
      fiscal_country: values.commercial_country || 'México',
      fiscal_country_other: values.commercial_country_other || '',
      fiscal_state: values.commercial_state || '',
      fiscal_city: values.commercial_city || '',
      fiscal_city_other: values.commercial_city_other || '',
      fiscal_postal_code: values.commercial_postal_code || ''
    });
    setFiscalCountryCode(commercialCountryCode);
    setFiscalStateValue(values.commercial_state || '');
  };

  const handleValuesChange = (changedValues) => {
    if (Object.prototype.hasOwnProperty.call(changedValues, 'commercial_country')) {
      const nextCountry = changedValues.commercial_country || 'México';
      const isCustom = nextCountry === CUSTOM_OPTION_VALUE;
      setCommercialUseCustomCountry(isCustom);
      setCommercialCountryCode(isCustom ? null : getCountryCode(nextCountry) || null);
      setCommercialStateValue('');
      setCommercialUseCustomState(false);
      setCommercialUseCustomCity(false);
      form.setFieldsValue({
        commercial_state: '',
        commercial_city: '',
        commercial_city_other: '',
        commercial_country_other: ''
      });
      if (useSameFiscalAddress) {
        syncFiscalWithCommercial();
      }
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'commercial_state')) {
      const nextState = changedValues.commercial_state || '';
      const selectedCountry = form.getFieldValue('commercial_country');
      const effectiveCountryCode = selectedCountry === CUSTOM_OPTION_VALUE
        ? null
        : getCountryCode(selectedCountry) || commercialCountryCode;
      setCommercialStateValue(nextState);
      setCommercialCityOptions(getCityOptions(effectiveCountryCode, nextState));
      form.setFieldsValue({ commercial_city: '', commercial_city_other: '' });
      setCommercialUseCustomCity(false);
      if (useSameFiscalAddress) {
        syncFiscalWithCommercial();
      }
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'commercial_city')) {
      const nextCity = changedValues.commercial_city || '';
      const isCustomCity = nextCity === CUSTOM_OPTION_VALUE;
      setCommercialUseCustomCity(isCustomCity);
      if (!isCustomCity) {
        form.setFieldsValue({ commercial_city_other: '' });
      }
      if (useSameFiscalAddress) {
        syncFiscalWithCommercial();
      }
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'commercial_address')
      || Object.prototype.hasOwnProperty.call(changedValues, 'commercial_postal_code')) {
      if (useSameFiscalAddress) {
        syncFiscalWithCommercial();
      }
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'fiscal_country') && !useSameFiscalAddress) {
      const nextCountry = changedValues.fiscal_country || 'México';
      const isCustom = nextCountry === CUSTOM_OPTION_VALUE;
      setFiscalUseCustomCountry(isCustom);
      setFiscalCountryCode(isCustom ? null : getCountryCode(nextCountry) || null);
      setFiscalStateValue('');
      setFiscalUseCustomState(false);
      setFiscalUseCustomCity(false);
      form.setFieldsValue({
        fiscal_state: '',
        fiscal_city: '',
        fiscal_city_other: '',
        fiscal_country_other: ''
      });
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'fiscal_state') && !useSameFiscalAddress) {
      const nextState = changedValues.fiscal_state || '';
      const selectedCountry = form.getFieldValue('fiscal_country');
      const effectiveCountryCode = selectedCountry === CUSTOM_OPTION_VALUE
        ? null
        : getCountryCode(selectedCountry) || fiscalCountryCode;
      setFiscalStateValue(nextState);
      setFiscalCityOptions(getCityOptions(effectiveCountryCode, nextState));
      form.setFieldsValue({ fiscal_city: '', fiscal_city_other: '' });
      setFiscalUseCustomCity(false);
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, 'fiscal_city') && !useSameFiscalAddress) {
      const nextCity = changedValues.fiscal_city || '';
      const isCustomCity = nextCity === CUSTOM_OPTION_VALUE;
      setFiscalUseCustomCity(isCustomCity);
      if (!isCustomCity) {
        form.setFieldsValue({ fiscal_city_other: '' });
      }
    }
  };

  const openPreview = (doc, title) => {
    if (!doc) return;
    setPreviewDoc(doc);
    setPreviewTitle(title || doc.file_name || t('shared.labels.document'));
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewDoc(null);
    setPreviewTitle('');
  };

  const renderDocumentThumbnail = (doc) => {
    if (!doc) return null;
    const fileUrl = doc.file_url || doc.file_path || `${getApiUrl()}${doc.file_path}`;
    const fileName = doc?.file_name || t('shared.labels.document');
    const ext = fileName.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return <img src={fileUrl} alt={fileName} className="document-thumbnail-image" />;
    }

    if (ext === 'pdf') {
      return (
        <object data={fileUrl} type="application/pdf" className="document-thumbnail-frame">
          <div className="document-thumbnail-fallback">
            <IconFile />
            <small>PDF</small>
          </div>
        </object>
      );
    }

    return (
      <div className="document-thumbnail-fallback">
        <IconFile />
        <small>{ext.toUpperCase()}</small>
      </div>
    );
  };

  const handleUpload = async (file, documentKey) => {
    if (previewMode) {
      setDocuments((prev) => ({
        ...prev,
        [documentKey]: {
          file_name: file?.name || t('shared.labels.document'),
          file_size: file?.size || 0,
          file_url: '',
          file_path: ''
        }
      }));
      Message.success(t('shared.messages.simulationDocUploaded'));
      return false;
    }

    setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));

    uploadMutation.mutate({ file, documentType: documentKey }, {
      onSuccess: (response) => {
        const uploadedDocument = response?.data?.document;
        if (!uploadedDocument) {
          Message.error(t('shared.messages.documentNoInfo'));
          setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
          return;
        }

        setDocuments((prev) => ({
          ...prev,
          [documentKey]: uploadedDocument
        }));
        Message.success(t('shared.messages.documentUploaded'));
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
      },
      onError: (error) => {
        console.error('Error al subir documento:', error);
        Message.error(t('shared.messages.documentUploadError'));
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
      }
    });

    return false;
  };

  const handleDelete = async (documentKey) => {
    if (previewMode) {
      setDocuments((prev) => {
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      Message.success(t('shared.messages.simulationDocDeleted'));
      return;
    }

    deleteMutation.mutate(documentKey, {
      onSuccess: () => {
        setDocuments((prev) => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
        Message.success(t('shared.messages.documentDeleted'));
      },
      onError: (error) => {
        console.error('Error al eliminar documento:', error);
        Message.error(t('shared.messages.documentDeleteError'));
      }
    });
  };

  const renderUploadCard = ({ key, label, description, required, changeMessage }) => {
    const isUploaded = !!documents[key];
    const progress = uploadProgress[key];
    const cardClassName = `document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`;

    return (
      <Card className={cardClassName} bordered>
        <div className="document-card-header">
          <div className="document-title">
            <IconFile />
            <span>{label}</span>
            {required && <Tag color="red">{t('shared.labels.required')}</Tag>}
          </div>
          {changeMessage && <div className="document-change-note">{changeMessage}</div>}
        </div>
        <div className="document-description">{description}</div>

        {progress ? (
          <Progress percent={progress} showText />
        ) : (
          <div className="document-actions">
            {isUploaded ? (
              <>
                <div className="document-info">
                  <div
                    className="document-thumbnail"
                    onClick={() => openPreview(documents[key], label)}
                    aria-label={t('shared.labels.viewDocument')}
                  >
                    {renderDocumentThumbnail(documents[key])}
                  </div>
                  <div className="document-meta">
                    <small>{documents[key].file_name}</small>
                    <small>{(documents[key].file_size / 1024).toFixed(1)} KB</small>
                  </div>
                </div>
                <Space>
                  <Button
                    type="primary"
                    icon={<IconCheck />}
                    onClick={() => openPreview(documents[key], label)}
                  >
                    {t('shared.labels.view')}
                  </Button>
                  <Button
                    type="text"
                    status="danger"
                    icon={<IconClose />}
                    onClick={() => handleDelete(key)}
                  >
                    {t('shared.labels.delete')}
                  </Button>
                </Space>
              </>
            ) : (
              <Upload
                showUploadList={false}
                beforeUpload={(file) => handleUpload(file, key)}
                accept=".pdf,.jpg,.jpeg,.png"
              >
                <Button
                  icon={<IconUpload />}
                  loading={uploadMutation.isPending && uploadMutation.variables?.documentType === key}
                >
                  {t('shared.buttons.uploadDocument')}
                </Button>
              </Upload>
            )}
          </div>
        )}
      </Card>
    );
  };

  const buildCorporatePayload = (values) => {
    const commercialCountry = values.commercial_country === CUSTOM_OPTION_VALUE
      ? (values.commercial_country_other || '').trim()
      : (values.commercial_country || '').trim();
    const commercialCity = values.commercial_city === CUSTOM_OPTION_VALUE
      ? (values.commercial_city_other || '').trim()
      : (values.commercial_city || '').trim();

    const fiscalCountry = useSameFiscalAddress
      ? commercialCountry
      : (values.fiscal_country === CUSTOM_OPTION_VALUE
        ? (values.fiscal_country_other || '').trim()
        : (values.fiscal_country || '').trim());
    const fiscalCity = useSameFiscalAddress
      ? commercialCity
      : (values.fiscal_city === CUSTOM_OPTION_VALUE
        ? (values.fiscal_city_other || '').trim()
        : (values.fiscal_city || '').trim());

    return {
      company_legal_name: values.company_legal_name?.trim(),
      trade_name: values.trade_name?.trim() || null,
      rfc: values.rfc?.toUpperCase()?.trim(),
      industry: values.industry?.trim() || null,
      website: values.website?.trim() || null,
      commercial_address: values.commercial_address?.trim(),
      commercial_city: commercialCity,
      commercial_state: values.commercial_state?.trim(),
      commercial_postal_code: values.commercial_postal_code?.trim(),
      commercial_country: commercialCountry || 'México',
      fiscal_address: useSameFiscalAddress
        ? values.commercial_address?.trim()
        : values.fiscal_address?.trim(),
      fiscal_city: fiscalCity,
      fiscal_state: useSameFiscalAddress
        ? values.commercial_state?.trim()
        : values.fiscal_state?.trim(),
      fiscal_postal_code: useSameFiscalAddress
        ? values.commercial_postal_code?.trim()
        : values.fiscal_postal_code?.trim(),
      fiscal_country: fiscalCountry || 'México'
    };
  };

  const handleSubmit = async (values) => {
    const payload = buildCorporatePayload(values);
    if (!payload.company_legal_name || !payload.rfc || !payload.commercial_address) {
      Message.error(t('steps.corporate.messages.completeMandatoryFields'));
      return;
    }

    if (!payload.commercial_country || !payload.commercial_state || !payload.commercial_city || !payload.commercial_postal_code) {
      Message.error(t('steps.corporate.messages.completeCommercialAddress'));
      return;
    }

    if (!payload.fiscal_address || !payload.fiscal_country || !payload.fiscal_state || !payload.fiscal_city || !payload.fiscal_postal_code) {
      Message.error(t('steps.corporate.messages.completeFiscalAddress'));
      return;
    }

    if (!RFC_REGEX.test(payload.rfc || '')) {
      Message.error(t('steps.corporate.messages.rfcInvalid'));
      return;
    }

    if (previewMode) {
      Message.success(t('steps.corporate.messages.simulationValidated'));
      onNext(null);
      return;
    }

    saveMutation.mutate(payload, {
      onSuccess: () => {
        Message.success(t('steps.corporate.messages.corporateSaved'));
        onNext(null);
      },
      onError: (error) => {
        console.error('Error al guardar datos corporativos:', error);
        Message.error(t('shared.messages.errorSaving'));
      }
    });
  };

  const handleSaveDraft = async () => {
    if (previewMode) {
      Message.success(t('shared.messages.simulationDraftSaved'));
      return;
    }

    const values = form.getFieldsValue();
    const payload = buildCorporatePayload(values);
    saveMutation.mutate(payload, {
      onSuccess: () => {
        Message.success(t('shared.messages.dataSavedDraft'));
      },
      onError: (error) => {
        console.error('Error al guardar borrador:', error);
        Message.error(t('shared.messages.errorSavingDraft'));
      }
    });
  };

  return (
    <div className="teacher-details-step">
      <div className="step-header">
        <h2>{t('steps.corporate.title')}</h2>
        <p>{t('steps.corporate.description')}</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onSubmit={handleSubmit}
        onValuesChange={handleValuesChange}
        autoComplete="off"
      >
        <div className="form-section">
            <h3>
            <IconHome style={{ marginRight: 8 }} />
            {t('steps.corporate.labels.companyInfo')}
          </h3>

          <FormItem
            label={t('steps.corporate.labels.legalName')}
            field="company_legal_name"
            rules={[{ required: true, message: t('steps.corporate.validation.legalNameRequired') }]}
            {...getFieldProps('company_legal_name')}
          >
            <Input placeholder={t('steps.corporate.placeholders.legalName')} />
          </FormItem>

          <FormItem
            label={t('steps.corporate.labels.tradeName')}
            field="trade_name"
            {...getFieldProps('trade_name')}
          >
            <Input placeholder={t('steps.corporate.placeholders.tradeName')} />
          </FormItem>

          <FormItem
            label={t('steps.corporate.labels.rfcMoral')}
            field="rfc"
            rules={[
              { required: true, message: t('steps.corporate.validation.rfcRequired') },
              { match: RFC_REGEX, message: t('steps.corporate.validation.rfcInvalid') }
            ]}
            {...getFieldProps('rfc')}
          >
            <Input placeholder={t('steps.corporate.placeholders.rfc')} maxLength={12} onChange={(value) => form.setFieldValue('rfc', value.toUpperCase())} />
          </FormItem>

          <FormItem label={t('steps.corporate.labels.industry')} field="industry" {...getFieldProps('industry')}>
            <Input placeholder={t('steps.corporate.placeholders.industry')} />
          </FormItem>

          <FormItem label={t('steps.corporate.labels.website')} field="website" {...getFieldProps('website')}>
            <Input placeholder={t('steps.corporate.placeholders.website')} />
          </FormItem>
        </div>

        <div className="form-section">
          <h3>
            <IconHome style={{ marginRight: 8 }} />
            {t('steps.corporate.labels.commercialAddress')}
          </h3>

          <FormItem
            label={t('steps.corporate.labels.commercialAddressField')}
            field="commercial_address"
            rules={[{ required: true, message: t('steps.corporate.validation.commercialAddressRequired') }]}
            {...getFieldProps('commercial_address')}
          >
            <Input placeholder={t('steps.corporate.placeholders.address')} />
          </FormItem>

          <div className="form-row">
            <FormItem
              label={t('steps.corporate.labels.country')}
              field="commercial_country"
              rules={[{ required: true, message: t('steps.corporate.validation.countryRequired') }]}
              {...getFieldProps('commercial_country')}
            >
              <Select showSearch>
                {COUNTRY_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
                <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.corporate.labels.state')}
              field="commercial_state"
              rules={[{ required: true, message: t('steps.corporate.validation.stateRequired') }]}
              {...getFieldProps('commercial_state')}
              extra={
                commercialUseCustomState && commercialStateOptions.length > 0 ? (
                  <Button
                    type="text"
                    size="mini"
                    className="custom-field-toggle"
                    onClick={() => {
                      setCommercialUseCustomState(false);
                      setCommercialStateValue('');
                      form.setFieldValue('commercial_state', '');
                    }}
                  >
                    {t('steps.corporate.labels.chooseFromList')}
                  </Button>
                ) : null
              }
            >
              {commercialUseCustomState || commercialStateOptions.length === 0 ? (
                <Input placeholder={t('steps.corporate.placeholders.writeState')} />
              ) : (
                <Select
                  placeholder={t('steps.corporate.placeholders.selectState')}
                  showSearch
                  loading={commercialCountryCode === 'MX' && commercialStatesQuery.isLoading}
                  onChange={(value) => {
                    if (value === CUSTOM_OPTION_VALUE) {
                      setCommercialUseCustomState(true);
                      form.setFieldValue('commercial_state', '');
                      return;
                    }
                    setCommercialStateValue(value);
                  }}
                >
                  {commercialStateOptions.map((state) => (
                    <Option key={state.value} value={state.value}>
                      {state.label}
                    </Option>
                  ))}
                  <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
                </Select>
              )}
            </FormItem>
          </div>

          {commercialUseCustomCountry && (
            <FormItem
              label={t('steps.corporate.labels.specifyCountry')}
              field="commercial_country_other"
              rules={[{ required: true, message: t('steps.corporate.validation.specifyCountry') }]}
            >
              <Input placeholder={t('steps.corporate.placeholders.enterCountry')} />
            </FormItem>
          )}

          <div className="form-row">
            <FormItem
              label={t('steps.corporate.labels.city')}
              field="commercial_city"
              rules={[{ required: true, message: t('steps.corporate.validation.cityRequired') }]}
              {...getFieldProps('commercial_city')}
            >
              <Select placeholder={t('steps.corporate.placeholders.selectCity')} showSearch>
                {commercialCityOptions.map((city) => (
                  <Option key={city.value} value={city.value}>
                    {city.label}
                  </Option>
                ))}
                <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.corporate.labels.postalCode')}
              field="commercial_postal_code"
              rules={[{ required: true, message: t('steps.corporate.validation.postalCodeRequired') }]}
              {...getFieldProps('commercial_postal_code')}
            >
              <Input placeholder={t('steps.corporate.placeholders.postalCode')} maxLength={10} />
            </FormItem>
          </div>

          {commercialUseCustomCity && (
            <FormItem
              label={t('steps.corporate.labels.specifyCity')}
              field="commercial_city_other"
              rules={[{ required: true, message: t('steps.corporate.validation.specifyCity') }]}
            >
              <Input placeholder={t('steps.corporate.placeholders.enterCity')} />
            </FormItem>
          )}
        </div>

        <div className="form-section">
          <h3>
            <IconHome style={{ marginRight: 8 }} />
            {t('steps.corporate.labels.fiscalAddress')}
          </h3>

          <Checkbox
            checked={useSameFiscalAddress}
            onChange={(value) => {
              setUseSameFiscalAddress(value);
              if (value) {
                syncFiscalWithCommercial();
              }
            }}
            style={{ marginBottom: 12 }}
          >
            {t('steps.corporate.labels.useSameAddress')}
          </Checkbox>

          <FormItem
            label={t('steps.corporate.labels.fiscalAddressField')}
            field="fiscal_address"
            rules={[{ required: true, message: t('steps.corporate.validation.fiscalAddressRequired') }]}
            {...getFieldProps('fiscal_address')}
          >
            <Input placeholder={t('steps.corporate.placeholders.address')} disabled={useSameFiscalAddress} />
          </FormItem>

          <div className="form-row">
            <FormItem
              label={t('steps.corporate.labels.country')}
              field="fiscal_country"
              rules={[{ required: true, message: t('steps.corporate.validation.countryRequired') }]}
              {...getFieldProps('fiscal_country')}
            >
              <Select showSearch disabled={useSameFiscalAddress}>
                {COUNTRY_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
                <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.corporate.labels.state')}
              field="fiscal_state"
              rules={[{ required: true, message: t('steps.corporate.validation.stateRequired') }]}
              {...getFieldProps('fiscal_state')}
              extra={
                fiscalUseCustomState && fiscalStateOptions.length > 0 ? (
                  <Button
                    type="text"
                    size="mini"
                    className="custom-field-toggle"
                    onClick={() => {
                      setFiscalUseCustomState(false);
                      setFiscalStateValue('');
                      form.setFieldValue('fiscal_state', '');
                    }}
                    disabled={useSameFiscalAddress}
                  >
                    {t('steps.corporate.labels.chooseFromList')}
                  </Button>
                ) : null
              }
            >
              {fiscalUseCustomState || fiscalStateOptions.length === 0 ? (
                <Input placeholder={t('steps.corporate.placeholders.writeState')} disabled={useSameFiscalAddress} />
              ) : (
                <Select
                  placeholder={t('steps.corporate.placeholders.selectState')}
                  showSearch
                  disabled={useSameFiscalAddress}
                  loading={fiscalCountryCode === 'MX' && fiscalStatesQuery.isLoading}
                  onChange={(value) => {
                    if (value === CUSTOM_OPTION_VALUE) {
                      setFiscalUseCustomState(true);
                      form.setFieldValue('fiscal_state', '');
                      return;
                    }
                    setFiscalStateValue(value);
                  }}
                >
                  {fiscalStateOptions.map((state) => (
                    <Option key={state.value} value={state.value}>
                      {state.label}
                    </Option>
                  ))}
                  <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
                </Select>
              )}
            </FormItem>
          </div>

          {fiscalUseCustomCountry && (
            <FormItem
              label={t('steps.corporate.labels.specifyCountry')}
              field="fiscal_country_other"
              rules={[{ required: true, message: t('steps.corporate.validation.specifyCountry') }]}
            >
              <Input placeholder={t('steps.corporate.placeholders.enterCountry')} disabled={useSameFiscalAddress} />
            </FormItem>
          )}

          <div className="form-row">
            <FormItem
              label={t('steps.corporate.labels.city')}
              field="fiscal_city"
              rules={[{ required: true, message: t('steps.corporate.validation.cityRequired') }]}
              {...getFieldProps('fiscal_city')}
            >
              <Select placeholder={t('steps.corporate.placeholders.selectCity')} showSearch disabled={useSameFiscalAddress}>
                {fiscalCityOptions.map((city) => (
                  <Option key={city.value} value={city.value}>
                    {city.label}
                  </Option>
                ))}
                <Option value={CUSTOM_OPTION_VALUE}>{t('steps.corporate.labels.other')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.corporate.labels.postalCode')}
              field="fiscal_postal_code"
              rules={[{ required: true, message: t('steps.corporate.validation.postalCodeRequired') }]}
              {...getFieldProps('fiscal_postal_code')}
            >
              <Input placeholder={t('steps.corporate.placeholders.postalCode')} maxLength={10} disabled={useSameFiscalAddress} />
            </FormItem>
          </div>

          {fiscalUseCustomCity && (
            <FormItem
              label={t('steps.corporate.labels.specifyCity')}
              field="fiscal_city_other"
              rules={[{ required: true, message: t('steps.corporate.validation.specifyCity') }]}
            >
              <Input placeholder={t('steps.corporate.placeholders.enterCity')} disabled={useSameFiscalAddress} />
            </FormItem>
          )}
        </div>

        <div className="form-section">
          <h3>
            <IconFile style={{ marginRight: 8 }} />
            {t('shared.documents.fiscalCertificateTitle')}
          </h3>
          {renderUploadCard({
            key: 'fiscal_certificate',
            label: t('shared.documents.fiscalCertificateTitle'),
            description: t('shared.documents.fiscalCertificateDesc'),
            required: true,
            changeMessage: documentChangeMap.fiscal_certificate
          })}
        </div>

        <div className="form-actions">
          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button size="large" onClick={onBack}>
              {t('shared.buttons.back')}
            </Button>
            <Button
              size="large"
              onClick={handleSaveDraft}
              loading={saveMutation.isPending}
              className="onboarding-draft-button"
            >
              {t('shared.buttons.saveDraft')}
            </Button>
            <Button type="primary" size="large" htmlType="submit" loading={saveMutation.isPending}>
              {t('shared.buttons.continue')}
            </Button>
            {previewMode && freeMode && (
              <Button size="large" onClick={() => onNext(null)}>
                {t('shared.buttons.skipValidation')}
              </Button>
            )}
            <ReportProblemButton stepLabel={t('steps.corporate.title')} previewMode={previewMode} asButton />
          </Space>
        </div>
      </Form>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={closePreview}
        document={previewDoc}
        title={previewTitle}
      />
    </div>
  );
}

export default CorporateDetailsStep;
